import CustomChart from '../CustomChart';
import ROASChart from '../ROASChart';
import useI18n from '@/hooks/useI18n';
import { formatMoney, formatReturn } from '@/utils/numberUtils';
import ToolTip from '../ToolTip';
import LegendDot from './LegendDot';
import {
  EMV_COLOR,
  SALES_COLOR,
  SPENDING_COLOR,
  ROAS_ALL_COLOR,
  ROAS_INSTAGRAM_COLOR,
  ROAS_TIKTOK_COLOR,
} from './analyticsTokens';

/**
 * Data needed for the EMV/Sales/Spending area chart. Brand-aggregate page
 * sources this from /chart-analysis2; Individual page from /campaignChart.
 * Both endpoints expose the same field names.
 */
export interface ChartAnalysisEmvData {
  labels?: string[];
  // Backend ships `null` for future-pad buckets; consumers must distinguish
  // those from real 0s so the line chart can break vs render at baseline.
  emvData?: (number | null)[];
  salesData?: (number | null)[];
  spendingData?: (number | null)[];
  totalEmv?: number | string;
  /** Index of "now" in `labels`. Used for the highlight marker. Null when
   *  today falls outside the rendered window (past-only custom range). */
  currentLabelIndex?: number | null;
}

export interface ChartAnalysisRoasData {
  labels?: string[];
  allRoas?: (number | null)[];
  instagramRoas?: (number | null)[];
  tiktokRoas?: (number | null)[];
  averageRoasOfTotal?: number;
  averageRoasOfInstagram?: number;
  averageRoasOfTiktok?: number;
  currentLabelIndex?: number | null;
}

interface ChartAnaylysisComponentProps {
  earnedMedia?: ChartAnalysisEmvData | null;
  roas?: ChartAnalysisRoasData | null;
}

const ChartAnaylysisComponent = ({
  earnedMedia,
  roas,
}: ChartAnaylysisComponentProps) => {
  const { t } = useI18n();

  // Strip year off labels like "Jan 2026" → "Jan" so the X-axis shows just
  // the month per design. Multi-year spans can repeat month names (e.g. two
  // "Dec" in an 18-month window) — accepted trade-off for cleaner labels.
  const rawEmvLabels = earnedMedia?.labels ?? [];
  const emvLabels = rawEmvLabels.map((label) =>
    typeof label === 'string' ? label.replace(/\s+\d{4}$/, '') : label,
  );
  const emvSpendingData = earnedMedia?.spendingData ?? [];
  const emvEmvData = earnedMedia?.emvData ?? [];
  const emvSalesData = earnedMedia?.salesData ?? [];
  const totalEmv = Number(earnedMedia?.totalEmv ?? 0);

  const rawRoasLabels = roas?.labels ?? [];
  const roasLabels = rawRoasLabels.map((label) =>
    typeof label === 'string' ? label.replace(/\s+\d{4}$/, '') : label,
  );
  const allRoas = roas?.allRoas ?? [];
  const instagramRoas = roas?.instagramRoas ?? [];
  const tiktokRoas = roas?.tiktokRoas ?? [];
  const avgRoasTotal = roas?.averageRoasOfTotal ?? 0;

  const chartSeries = [
    { name: t('common.earnedMediaValue'), data: emvEmvData },
    { name: t('common.sales'), data: emvSalesData },
    { name: t('common.campaignSpending'), data: emvSpendingData },
  ];

  const emvTooltipData = ['EMV €', 'Sales €', 'Spending €'];

  // Use the backend's authoritative `currentLabelIndex` — derived from the
  // resource's date math, not from local Date.toLocaleString() which would
  // mis-match localized labels (e.g. French "mai" vs English "May") and
  // could drift on non-UTC servers. Null means today falls outside the
  // rendered window (past-only custom range) → no highlight.
  const highlightCategoryIndex = earnedMedia?.currentLabelIndex ?? undefined;
  const roasHighlightCategoryIndex = roas?.currentLabelIndex ?? undefined;
  const maxEarnedSellValue = (() => {
    const values = [
      ...(emvSpendingData || []),
      ...(emvEmvData || []),
      ...(emvSalesData || []),
    ].filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (!values.length) return 10;
    return Math.max(...values) + 10;
  })();

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <div className="flex items-center" style={{ gap: 4 }}>
        <h2
          className="font-medium opacity-90"
          style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: 14,
            lineHeight: '120%',
            color: '#121111',
          }}
        >
          {t('common.chartAnalysis')}
        </h2>
      </div>
      <div className="flex" style={{ gap: 16 }}>
        <div
          className="bg-white border border-[#E5E9EB] flex flex-col"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 376,
            maxHeight: 438,
            borderRadius: 24,
            padding: 24,
            gap: 16,
            boxShadow: '0px 21px 94px 0px #00000008',
          }}
        >
          <div
            className="flex justify-between items-start"
            style={{ height: 49 }}
          >
            <div className="flex flex-col" style={{ gap: 4 }}>
              <div
                style={{
                  color: '#9B919D',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 12,
                  lineHeight: '16px',
                  fontWeight: 400,
                }}
              >
                {t('common.earnedMediaValue')}{' '}
                <ToolTip
                  text={String(t('common.EMVTooltipDesc'))}
                  position="top"
                  className="tooltip-multiline"
                >
                  <span className="inline-block">ⓘ</span>
                </ToolTip>
              </div>
              <div
                style={{
                  color: '#292828',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 24,
                  lineHeight: '24px',
                  fontWeight: 500,
                }}
              >
                {formatMoney(Number(totalEmv))}
              </div>
            </div>
            <div className="flex items-center" style={{ gap: 16 }}>
              <LegendDot
                color={EMV_COLOR}
                label={String(t('common.earnedMediaValue'))}
                size={12}
                gap={8}
              />
              <LegendDot
                color={SALES_COLOR}
                label={String(t('common.sales'))}
                size={12}
                gap={8}
              />
              <LegendDot
                color={SPENDING_COLOR}
                label={String(t('common.campaignSpending'))}
                size={12}
                gap={8}
              />
            </div>
          </div>
          <CustomChart
            chartSeries={chartSeries}
            categories={emvLabels}
            TooltipData={emvTooltipData}
            maXValue={maxEarnedSellValue === 10 ? 15000000 : maxEarnedSellValue}
            hideGrid={false}
            hideYAxisLines={true}
            rotateLabels={false}
            colors={[EMV_COLOR, SALES_COLOR, SPENDING_COLOR]}
            highlightCategoryIndex={highlightCategoryIndex}
          />
        </div>
        <div
          className="bg-white border border-[#E5E9EB] flex flex-col"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 376,
            maxHeight: 438,
            borderRadius: 24,
            padding: 24,
            gap: 16,
            boxShadow: '0px 21px 94px 0px #00000008',
          }}
        >
          <div
            className="flex justify-between items-start"
            style={{ height: 49 }}
          >
            <div className="flex flex-col" style={{ gap: 4 }}>
              <div
                style={{
                  color: '#9B919D',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 12,
                  lineHeight: '16px',
                  fontWeight: 400,
                }}
              >
                {t('common.returnOnAdSpend')}{' '}
                <ToolTip
                  text={String(t('common.ROASTooltipDesc'))}
                  position="top"
                >
                  <span className="inline-block">ⓘ</span>
                </ToolTip>
              </div>
              <div
                style={{
                  color: '#292828',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 24,
                  lineHeight: '24px',
                  fontWeight: 500,
                }}
              >
                {formatReturn(avgRoasTotal)}
              </div>
            </div>
            <div className="flex items-center" style={{ gap: 16 }}>
              <LegendDot
                color={ROAS_ALL_COLOR}
                label={String(t('common.roas'))}
                size={12}
                gap={8}
              />
              <LegendDot
                color={ROAS_INSTAGRAM_COLOR}
                label={String(t('common.instagramRoas'))}
                size={12}
                gap={8}
              />
              <LegendDot
                color={ROAS_TIKTOK_COLOR}
                label={String(t('common.tiktokRoas'))}
                size={12}
                gap={8}
              />
            </div>
          </div>
          <ROASChart
            allRoas={allRoas}
            instagramRoas={instagramRoas}
            tiktokRoas={tiktokRoas}
            categories={roasLabels}
            rotateLabels={false}
            highlightCategoryIndex={roasHighlightCategoryIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartAnaylysisComponent;
